'use client';

/**
 * Wrapper for static legal/about pages. Picks the EN or TH content tree based
 * on the active i18n locale and renders it inside a centered article column
 * with the project's typography tokens.
 *
 * Server pages pass already-rendered JSX (server components) as `en`/`th`
 * props; this wrapper only swaps which one is shown — no hydration cost on
 * the content trees themselves.
 */

import Link from 'next/link';
import { useI18n, useT } from '@/lib/i18n';

export default function LegalPage({
  en,
  th,
}: {
  en: React.ReactNode;
  th: React.ReactNode;
}) {
  const { locale } = useI18n();
  const t = useT();

  return (
    <article
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: '48px 24px 64px',
      }}
    >
      <Link
        href="/"
        style={{
          color: 'var(--ink-2)',
          fontSize: 14,
          textDecoration: 'none',
          marginBottom: 24,
          display: 'inline-block',
        }}
      >
        ← {t('legal.back_home')}
      </Link>
      <div className="legal-prose">
        {locale === 'th' ? th : en}
      </div>
      <style>{`
        .legal-prose h1 {
          font-family: var(--font-headline);
          font-size: 40px;
          line-height: 1.15;
          margin: 0 0 8px;
        }
        .legal-prose .last-updated {
          color: var(--ink-3);
          font-size: 13px;
          margin-bottom: 32px;
        }
        .legal-prose h2 {
          font-family: var(--font-headline);
          font-size: 22px;
          margin: 32px 0 12px;
        }
        .legal-prose h3 {
          font-size: 16px;
          font-weight: 600;
          margin: 20px 0 8px;
        }
        .legal-prose p,
        .legal-prose li {
          color: var(--ink-2);
          line-height: 1.7;
          font-size: 15px;
        }
        .legal-prose ul {
          padding-left: 20px;
          margin: 12px 0;
        }
        .legal-prose li {
          margin-bottom: 6px;
        }
        .legal-prose a {
          color: var(--accent);
          text-decoration: underline;
        }
        .legal-prose strong {
          color: var(--ink);
        }
      `}</style>
    </article>
  );
}
